import api from './client';

export type ScenePlanPayload = {
  title: string;
  plannedDate?: string;
  plannedTime?: string;
  estimatedDurationMinutes?: number;
  sceneType?: string;
  plannedActivities?: number[];
  equipmentIds?: number[];
  description?: string;
  descriptionHtml?: string;
  aftercarePlan?: string;
  negotiationNotes?: string;
  notes?: string;
  aftercareChecklistId?: number;
};

export const scenePlanAPI = {
  create: (data: ScenePlanPayload) => api.post('/scene-plans', data),

  list: () => api.get('/scene-plans'),

  // Spec 318: the limits catalog minus anything that is a hard limit for the
  // caller or a linked partner, plus a count of what was withheld. Use this,
  // NOT limitsAPI.getActivities(), for any picker feeding planned_activities —
  // the raw catalog offers choices createPlan/updatePlan will refuse.
  eligibleActivities: () => api.get('/scene-plans/eligible-activities'),

  get: (id: string) => api.get(`/scene-plans/${id}`),

  update: (id: string, data: ScenePlanPayload) =>
    api.put(`/scene-plans/${id}`, data),

  delete: (id: string) => api.delete(`/scene-plans/${id}`),

  // Spec 313: overrideReason is required only when the plan is currently
  // over the org's configured intensity budget — the backend 400s if it's
  // over budget and no reason was supplied. Every existing (non-composer)
  // caller passes only `id` -- keep that call shape as a genuine one-arg
  // api.post() (not `api.post(url, undefined)`) so it stays byte-for-byte
  // what scenePlans.test.ts's `toHaveBeenCalledWith('/scene-plans/plan-1/confirm')`
  // already asserts.
  confirm: (id: string, overrideReason?: string) =>
    overrideReason
      ? api.post(`/scene-plans/${id}/confirm`, { overrideReason })
      : api.post(`/scene-plans/${id}/confirm`),

  limitsCheck: (id: string) => api.get(`/scene-plans/${id}/limits-check`),

  // Spec 313 — Scene Composer add/remove. One activity per call (not a
  // full-array replace) -- the backend takes a row lock and read-modifies-
  // writes under it, closing the concurrent-add race a client-computed full
  // array would be exposed to. Feature-flag gated ('scene_composer').
  addActivity: (id: string, activityId: number) =>
    api.patch(`/scene-plans/${id}/activities`, { activityId, action: 'add' }),

  removeActivity: (id: string, activityId: number) =>
    api.patch(`/scene-plans/${id}/activities`, { activityId, action: 'remove' }),

  linkToScene: (id: string, sceneEntryId: string) =>
    api.post(`/scene-plans/${id}/link-scene`, { sceneEntryId }),

  // Sharing
  share: (id: string, userIds: number[], permission?: 'view' | 'edit') =>
    api.post(`/scene-plans/${id}/shares`, { userIds, permission }),

  getShares: (id: string) => api.get(`/scene-plans/${id}/shares`),

  removeShare: (id: string, userId: number) =>
    api.delete(`/scene-plans/${id}/shares/${userId}`),

  getSharedWithMe: () => api.get('/scene-plans/shared-with-me'),

  // Spec 266 sub-slice 1: per-section partner review
  getSections: (id: string) => api.get(`/scene-plans/${id}/sections`),

  approveSection: (id: string, sectionKey: string) =>
    api.post(`/scene-plans/${id}/sections/${sectionKey}/approve`),

  postSectionMessage: (id: string, sectionKey: string, content: string) =>
    api.post(`/scene-plans/${id}/sections/${sectionKey}/messages`, { content }),
};

export const aftercareAPI = {
  // Template gallery
  getTemplates: () => api.get('/aftercare/templates'),

  // Checklist management
  getChecklist: () => api.get('/aftercare/checklist'),

  updateChecklist: (items: { key: string; label: string; category: string }[]) =>
    api.put('/aftercare/checklist', { items }),

  // Session lifecycle
  startSession: (data: {
    sceneEntryId: string;
    checklistId?: number;
    sessionItems?: { key: string; label: string; category: string }[];
  }) => api.post('/aftercare/sessions', data),

  getSession: (sessionId: number) =>
    api.get(`/aftercare/sessions/${sessionId}`),

  toggleItem: (sessionId: number, itemKey: string) =>
    api.patch(`/aftercare/sessions/${sessionId}/toggle`, { itemKey }),

  completeSession: (sessionId: number, data: { subFeeling: string; domFeeling?: string }) =>
    api.post(`/aftercare/sessions/${sessionId}/complete`, data),

  // Scene-linked lookup
  getSessionByScene: (sceneEntryId: string) =>
    api.get(`/aftercare/scenes/${sceneEntryId}/session`),
};
